import { APP_CLIENT_ID, APP_CLIENT_SECRET } from 'variables';
import {
  GRANT_TYPE,
  RequestStatusTypes,
  TxtTypes,
  TOKEN_TYPE_HINT,
} from 'consts';
import { takeEvery, call, put, select } from 'redux-saga/effects';
import ActionTypes from 'store/oauth/actionTypes';
import { ResponseFetcher } from 'utils/fetcher/types';
import {
  getTokenRequest,
  getIntrospectRequest,
  getLoginRequest,
} from 'store/oauth/requests';
import {
  setToken,
  setGetTokenRequestStatus,
  setIntrospect,
  setGetIntrospectRequestStatus,
  setForbiddenAccess,
  setGettingToken,
} from 'store/oauth/actions';
import {
  Token,
  Introspect,
  GetTokenRequest,
  GetIntrospectRequest,
  GetLoginRequest,
} from 'store/oauth/types';
import {
  makeSelectGettingToken,
  makeSelectTokenAccessToken,
} from 'store/oauth/selectors';
import { makeSelectUsername, makeSelectPassword } from 'store/app/selectors';
import { logOut } from 'utils/fetcher/tokenGuard';

export default function* sagas() {
  yield takeEvery(ActionTypes.GET_TOKEN_REQUEST, getTokenRequestSaga);
  yield takeEvery(ActionTypes.GET_TOKEN_REQUEST, getTokenRequestLoadingSaga);
  yield takeEvery(ActionTypes.GET_INTROSPECT_REQUEST, getIntrospectRequestSaga);
}

export function* getTokenRequestSaga() {
  try {
    yield put(setGettingToken(true));
    const username = yield select(makeSelectUsername());
    const password = yield select(makeSelectPassword());
    const gettingTokenStatus = yield select(makeSelectGettingToken());
    const payload: GetTokenRequest = {
      client_id: APP_CLIENT_ID,
      client_secret: APP_CLIENT_SECRET,
      username,
      password,
      grant_type: GRANT_TYPE,
    };
    const response: ResponseFetcher<Token> = yield call(
      getTokenRequest,
      payload
    );

    if (response.ok) {
      const loginPayload: GetLoginRequest = {
        usuario: username,
      };
      yield put(setToken(response.data));
      const loginLog: ResponseFetcher<boolean> = yield call(
        getLoginRequest,
        loginPayload
      );

      if (loginLog.ok && loginLog.data === true) {
        yield put(setForbiddenAccess(''));
        yield put(
          setGetTokenRequestStatus({
            status: RequestStatusTypes.Success,
            code: response.code,
            url: response.url,
          })
        );
        yield put(setGettingToken(false));
      } else {
        yield put(
          setGetTokenRequestStatus({
            status: RequestStatusTypes.Error,
            code: loginLog.code,
            message: TxtTypes.TXT_LOGIN_INPUT_ERROR,
            url: response.url,
          })
        );
        if (gettingTokenStatus) {
          logOut();
        }
        throw new Error(`Error: ${ActionTypes.GET_TOKEN_REQUEST}`);
      }
    } else {
      yield put(
        setGetTokenRequestStatus({
          status: RequestStatusTypes.Error,
          code: response.code,
          message: TxtTypes.TXT_LOGIN_INPUT_ERROR,
          url: response.url,
        })
      );
      if (gettingTokenStatus) {
        logOut();
      }
      throw new Error(`Error: ${ActionTypes.GET_TOKEN_REQUEST}`);
    }
  } catch (err) {
    yield put(
      setGetTokenRequestStatus({
        status: RequestStatusTypes.Error,
        message: TxtTypes.TXT_LOGIN_INPUT_ERROR,
      })
    );
    // I comment line because it is not appropriate for this error to appear in the console. Can be viewed by user
    // console.error(err);
  }
}

export function* getIntrospectRequestSaga() {
  try {
    const accessToken: string = yield select(makeSelectTokenAccessToken());
    const payload: GetIntrospectRequest = {
      client_id: APP_CLIENT_ID,
      client_secret: APP_CLIENT_SECRET,
      token: accessToken,
      token_type_hint: TOKEN_TYPE_HINT,
    };
    const response: ResponseFetcher<Introspect> = yield call(
      getIntrospectRequest,
      payload
    );

    if (response.ok) {
      yield put(setIntrospect(response.data));
      yield put(
        setGetIntrospectRequestStatus({
          status: RequestStatusTypes.Success,
          code: response.code,
          url: response.url,
        })
      );
    } else {
      yield put(
        setGetIntrospectRequestStatus({
          status: RequestStatusTypes.Error,
          code: response.code,
          message: TxtTypes.TXT_DEFAULT_MESSAGE_ERROR,
          url: response.url,
        })
      );
      throw new Error(`Error: ${ActionTypes.GET_INTROSPECT_REQUEST}`);
    }
  } catch (err) {
    console.error(err);
  }
}

export function* getTokenRequestLoadingSaga() {
  yield put(setGetTokenRequestStatus({ status: RequestStatusTypes.Loading }));
}
